import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { booleanPointInPolygon, circle, distance, point } from '@turf/turf';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';
import { showNotification } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import { DEFAULT_RADIUS_IN_KM } from '../../../constants';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { createTorusRingFeature, getZoneColor } from '../../../MultiFlightMap.helpers';
import { useMapPopover } from '../../MapPopover/MapPopover.context';
import { CircleConfirmation } from '../../ShapeConfirmation/CircleConfirmation';
import { getCircleDrawColors } from './circle.helpers';
import { CircleMode } from './modes/CircleMode';
import { DirectModeOverride } from './modes/DirectModeOverride';
import { DragCircleMode } from './modes/DragCircleMode';
import { SimpleSelectModeOverride } from './modes/SimpleSelectModeOverride';

import type { Feature, FeatureCollection, Geometry, MultiPolygon, Polygon } from 'geojson';
import type { MapMouseEvent } from 'mapbox-gl';
import type { CircleZoneFeatureProperties, MapMode } from '../../MapView/map.types';

type Props = {
  mapId: string;
};

export function CircleDrawControl({ mapId }: Props) {
  const { [mapId]: map } = useMap();
  const drawRef = useRef<MapboxDraw | null>(null);
  const previousEditedCircleIdRef = useRef<string | null>(null);
  const previousModeRef = useRef<MapMode | null>(null);

  const { formatMessage } = useIntl();
  const { circleZones, editedCircleId, mode, onAddCircleZone, setMode, torusZones, updateCircle } = useMultiFlightMap();
  const { closeMapPopover, openMapPopover } = useMapPopover();
  const { uiColors }: any = useTheme();

  const [torusCenter, setTorusCenter] = useState<[number, number] | null>(null);

  const cleanupDraw = () => {
    if (drawRef.current && map?.getMap() && map.getMap().hasControl(drawRef.current)) {
      try {
        drawRef.current.changeMode('simple_select');
        drawRef.current.deleteAll();
        map.getMap().removeControl(drawRef.current);
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: off
        console.warn('Error during MapboxDraw cleanup:', e);
      } finally {
        drawRef.current = null;
      }
    }
  };

  const openCircleConfirmation = (center: [number, number], radiusInKm: number) => {
    const mapInstance = map?.getMap();
    if (!mapInstance) {
      return;
    }
    const { x, y } = mapInstance.project(center);
    const rect = mapInstance.getContainer().getBoundingClientRect();

    openMapPopover({
      render: () => (
        <CircleConfirmation
          center={center}
          initialRadiusInKm={radiusInKm}
          onCancel={() => {
            cleanupDraw();
            setMode(null);
          }}
          onConfirm={async ({ name, radiusInKm: confirmedRadiusInKm }) => {
            cleanupDraw();
            setMode(null);
            try {
              await onAddCircleZone({
                center,
                name,
                radiusInKm: confirmedRadiusInKm
              });
            } catch {
              showNotification(
                'map',
                formatMessage({
                  defaultMessage: 'Could not create the location. Please try again.',
                  id: 'multiFlightLocations.confirmation.circle.error'
                })
              );
            }
          }}
        />
      ),
      x: x + rect.left,
      y: y + rect.top
    });
  };

  const saveChanges = async (idToSave: string | null = null) => {
    if (!drawRef.current) {
      return;
    }

    const data = drawRef.current.getAll();
    const feature = idToSave
      ? data.features.find(f => f.id === idToSave && f.geometry.type === 'Polygon' && f.properties?.isCircle)
      : data.features.find(
          (f): f is Feature<Polygon, CircleZoneFeatureProperties> =>
            f.geometry.type === 'Polygon' && f.properties?.isCircle
        );

    if (!feature || !feature.properties) {
      cleanupDraw();
      setMode(null);
      return;
    }

    const { radiusInKm } = feature.properties as CircleZoneFeatureProperties;
    const [lng, lat] = feature.properties.center;

    const isTransitioningFromEditToAdd = previousModeRef.current === 'edit-circle' && mode === 'add-circles';

    if (mode === 'add-circles' && !editedCircleId) {
      if (!isTransitioningFromEditToAdd) {
        // Defer cleanup until the user confirms or cancels in the popover.
        openCircleConfirmation([lng, lat], radiusInKm);
        return;
      }
      cleanupDraw();
      if (
        editedCircleId === previousEditedCircleIdRef.current &&
        previousModeRef.current !== 'draw-polygons' &&
        previousModeRef.current !== 'edit-polygon'
      ) {
        setMode(null);
      }
      return;
    }

    try {
      if (editedCircleId && feature.id === editedCircleId) {
        const zoneToUpdate = [...circleZones, ...torusZones].find(z => z._id === editedCircleId);
        if (zoneToUpdate) {
          await updateCircle({
            ...zoneToUpdate,
            latlng: {
              lat,
              lng
            },
            radius: radiusInKm * 1000
          });
        }
      }
    } finally {
      cleanupDraw();
      if (
        editedCircleId === previousEditedCircleIdRef.current &&
        previousModeRef.current !== 'draw-polygons' &&
        previousModeRef.current !== 'edit-polygon'
      ) {
        setMode(null);
      }
    }
  };

  const handleMapClick = (e: MapMouseEvent) => {
    if (!drawRef.current) {
      return;
    }
    const drawFeatures = drawRef.current.getAll().features;
    const clickedOnDrawFeature = drawFeatures.some(f => {
      if (!f || !f.geometry) {
        return false;
      }
      const isPolygonLike = (geom: Geometry): geom is Polygon | MultiPolygon => {
        return geom.type === 'Polygon' || geom.type === 'MultiPolygon';
      };
      if (!isPolygonLike(f.geometry)) {
        return false;
      }
      const polygonFeature: Feature<Polygon | MultiPolygon> = {
        geometry: f.geometry,
        properties: f.properties || {},
        type: 'Feature'
      };
      return booleanPointInPolygon(point([e.lngLat.lng, e.lngLat.lat]), polygonFeature);
    });

    if (!clickedOnDrawFeature) {
      saveChanges();
    }
  };

  const handleDocumentClick = (event: MouseEvent) => {
    const toolbarEl = document.querySelector('.toolbarMap');
    if (toolbarEl && toolbarEl.contains(event.target as Node)) {
      handleMapClick({
        lngLat: {
          lat: 0,
          lng: 0
        },
        originalEvent: event,
        target: map?.getMap(),
        type: 'click'
      } as unknown as MapMouseEvent);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveChanges();
    } else if (e.key === 'Escape') {
      closeMapPopover();
      cleanupDraw();
      setMode(null);
    }
  };

  const concentricCircleFeatures = useMemo<Feature<Polygon>[]>(() => {
    if (mode !== 'edit-circle' || !editedCircleId || !torusCenter) {
      return [];
    }
    const editedZone = torusZones.find(z => z._id === editedCircleId);
    if (!editedZone || !editedZone.latlng || !editedZone.parentTorus) {
      return [];
    }
    const concentricZones = torusZones
      .filter(z => z.latlng && z.parentTorus === editedZone.parentTorus && z._id !== editedCircleId)
      .sort((a, b) => b.radius! - a.radius!);

    if (concentricZones.length === 1) {
      const concentricZone = concentricZones[0];
      return [
        circle(torusCenter, concentricZone.radius! / 1000, {
          properties: {
            _id: concentricZone._id,
            color: getZoneColor(uiColors, concentricZone)
          },
          steps: 64,
          units: 'kilometers'
        })
      ] as Feature<Polygon>[];
    }

    return concentricZones
      .map((z, index) => {
        const outerRadius = z.radius!;
        const innerRadius = concentricZones[index + 1]?.radius ?? 0;
        if (innerRadius === 0) {
          return circle(torusCenter, outerRadius / 1000, {
            properties: {
              _id: z._id,
              color: getZoneColor(uiColors, z)
            },
            steps: 64,
            units: 'kilometers'
          });
        }

        return createTorusRingFeature(torusCenter, outerRadius, innerRadius, {
          _id: z._id,
          color: getZoneColor(uiColors, z)
        });
      })
      .filter((f): f is Feature<Polygon> => !!f && !!f.geometry && f.geometry?.type === 'Polygon');
  }, [torusCenter, mode, editedCircleId, circleZones, uiColors]);

  useEffect(() => {
    const mapInstance = map?.getMap();

    const currentMode = mode;
    const currentEditedCircleId = editedCircleId;
    const prevMode = previousModeRef.current;
    const prevEditedCircleId = previousEditedCircleIdRef.current;

    const isAddOrEdit = prevMode === 'add-circles' || prevMode === 'edit-circle';
    const modeChanged = prevMode !== currentMode;
    const editCircleChanged = prevMode === 'edit-circle' && prevEditedCircleId !== currentEditedCircleId;

    if (isAddOrEdit && (modeChanged || editCircleChanged)) {
      if (prevMode === 'edit-circle' && prevEditedCircleId) {
        saveChanges(prevEditedCircleId);
      } else if (prevMode === 'add-circles') {
        saveChanges();
      }
    }

    if (!mapInstance || (currentMode !== 'add-circles' && currentMode !== 'edit-circle')) {
      cleanupDraw();
      previousEditedCircleIdRef.current = currentEditedCircleId;
      previousModeRef.current = currentMode;
      return;
    }

    if (drawRef.current) {
      cleanupDraw();
    }

    const draw = new MapboxDraw({
      controls: {
        combine_features: false,
        line_string: false,
        point: false,
        polygon: false,
        trash: false,
        uncombine_features: false
      },
      defaultMode: 'draw_circle',
      modes: {
        ...MapboxDraw.modes,
        direct_select: DirectModeOverride,
        drag_circle: DragCircleMode,
        draw_circle: CircleMode,
        simple_select: SimpleSelectModeOverride
      },
      styles: getCircleDrawColors(uiColors),
      userProperties: true
    });

    drawRef.current = draw;

    if (!mapInstance.hasControl(draw)) {
      mapInstance.addControl(draw);
    }

    if (currentMode === 'edit-circle' && currentEditedCircleId) {
      const circleZone = [...circleZones, ...torusZones].find(z => z._id === currentEditedCircleId);
      if (circleZone?.latlng && typeof circleZone.radius === 'number') {
        const radiusInKm = circleZone.radius / 1000;
        const center = [circleZone.latlng.lng, circleZone.latlng.lat];
        const circlePolygon = circle(center, radiusInKm, {
          steps: 64,
          units: 'kilometers'
        });

        const circleFeature: Feature = {
          geometry: circlePolygon.geometry!,
          id: circleZone._id,
          properties: {
            center,
            disableMove: !!circleZone.shop,
            isCircle: true,
            radiusInKm,
            user_isCircle: true
          },
          type: 'Feature'
        };

        const addedFeatureIds = draw.add(circleFeature);
        if (addedFeatureIds.length > 0) {
          draw.changeMode('direct_select', {
            disableMove: !!circleZone.shop,
            featureId: addedFeatureIds[0]
          } as { disableMove?: boolean; featureId: string });
        }
      } else {
        cleanupDraw();
        setMode(null);
      }
    } else if (currentMode === 'add-circles') {
      const bounds = mapInstance.getBounds();
      let initialRadius = DEFAULT_RADIUS_IN_KM;

      if (bounds) {
        const centerLat = mapInstance.getCenter().lat;
        const westPoint = point([bounds.getWest(), centerLat]);
        const eastPoint = point([bounds.getEast(), centerLat]);
        const mapWidthInKm = distance(westPoint, eastPoint, { units: 'kilometers' });
        initialRadius = Math.max((mapWidthInKm * 0.1) / 2, 0.05);
      }
      draw.changeMode('draw_circle', { initialRadiusInKm: initialRadius });
    }

    function handleDrawUpdate() {
      if (mode !== 'edit-circle' || !editedCircleId) {
        return;
      }
      const editedZone = torusZones.find(z => z._id === editedCircleId);
      if (!editedZone) {
        return;
      }
      const { features } = drawRef.current!.getSelected();
      const updatedFeature = features[0];
      if (!updatedFeature || !updatedFeature.properties?.isCircle) {
        return;
      }

      const [lng, lat] = updatedFeature.properties.center;

      setTorusCenter(prevCenter => {
        const hasChange = !prevCenter || lng !== prevCenter[0] || lat !== prevCenter[1];
        return hasChange ? [lng, lat] : prevCenter;
      });
    }

    if (drawRef.current) {
      mapInstance.on('draw.render', handleDrawUpdate);
    }

    mapInstance.on('click', handleMapClick);
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleDocumentClick);

    previousEditedCircleIdRef.current = currentEditedCircleId;
    previousModeRef.current = currentMode;

    return () => {
      if (drawRef.current) {
        mapInstance.off('draw.render', handleDrawUpdate);
      }
      mapInstance.off('click', handleMapClick);
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleDocumentClick);
      cleanupDraw();
    };
  }, [map, mode, editedCircleId, circleZones, torusZones, uiColors]);

  const concentricCirclesFeatureCollection: FeatureCollection<Polygon> = {
    features: concentricCircleFeatures,
    type: 'FeatureCollection'
  };

  return concentricCircleFeatures.length > 0 ? (
    <Source data={concentricCirclesFeatureCollection} id="concentric-circles-edit-overlay" type="geojson">
      <Layer
        id="concentric-circles-edit-fill"
        paint={{
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.2
        }}
        type="fill"
      />
      <Layer
        id="concentric-circles-edit-outline"
        paint={{
          'line-color': ['get', 'color'],
          'line-width': 2
        }}
        type="line"
      />
    </Source>
  ) : null;
}
